import omit from 'lodash/omit';
import { useMemo } from 'react';
import { Button, Col, Form, FormLabel, Row } from 'react-bootstrap';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormRegister,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { ApiError, LicenseTypeEnum } from '../../../api';
import { CERTIFICATES_STATES } from '../../../utils/certificates-local-data';
import { ShowFieldErrors } from '../../../utils/forms';
import { FileUploader } from '../FileUploader';
import { FormFeedback } from '../FormFeedback';
import { FormFieldVisibility } from '../FormLabel';
import { FormTextField } from '../FormTextField';
import { SelectController } from '../SelectController';

interface LicensesUploaderProps {
  controlId: string;
  control: Control;
  errors: FieldErrors;
  showServerFieldErrors: ShowFieldErrors | undefined;
  serverErrors: ApiError | undefined;
  setShowServerFieldErrors: (showServerFieldErrors: ShowFieldErrors) => void;
  register: UseFormRegister<FieldValues>;
  licenseType: LicenseTypeEnum;
  itemCaption?: string;
  className?: string;
  removeButtonText?: string;
}

export const LicensesUploader = ({
  controlId,
  control,
  errors,
  serverErrors,
  showServerFieldErrors,
  setShowServerFieldErrors,
  register,
  licenseType,
  itemCaption = 'License',
  className,
  removeButtonText,
}: LicensesUploaderProps) => {
  const { setValue } = useFormContext();
  const controlIdName = useMemo(() => `${controlId}.${licenseType}`, [controlId, licenseType]);
  const emptyLicense = useMemo(
    () => ({
      type: licenseType,
      file__file: null,
      number: '',
      state: '',
      status: 'ACTIVE' as const,
      expiration_date: null,
    }),
    [licenseType]
  );
  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: controlIdName, // unique name for your Field Array
  });

  // Convert CERTIFICATES_STATES to array of options
  const stateOptions = Object.entries(CERTIFICATES_STATES).map(([value, label]) => ({
    value,
    label,
  }));

  return (
    <>
      <FormFeedback clientError={errors?.licenses?.[licenseType]} />
      <ul className="list-unstyled">
        {fields.map((item, index) => (
          <li key={item.id} className="mb-3 border bg-light rounded p-3">
            <Row className="align-items-start">
              <Col xs={12}>
                <Row>
                  <Col>
                    <span className="text-primary fw-bold">
                      {itemCaption}
                      {item.state ? `: ${item.state}` : ' (New)'}
                    </span>
                  </Col>
                  <Col xs="auto" className="ps-0">
                    <Button
                      className="text-danger p-0"
                      type="button"
                      variant="link"
                      size="lg"
                      onClick={() => remove(index)}
                    >
                      <BsTrash />
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6} md={3}>
                    <FormLabel
                      className="text-field-label"
                      visibility={FormFieldVisibility.CONNECTED}
                    >
                      State<span className="text-danger"> *</span>
                    </FormLabel>
                    <SelectController
                      name={`${controlIdName}[${index}].state`}
                      control={control}
                      options={stateOptions}
                      isClearable={false}
                      isMulti={false}
                      placeholder="Choose One"
                      isInvalid={
                        !!errors?.[controlId]?.[licenseType]?.[index]?.state ||
                        !!showServerFieldErrors?.[controlId]?.[licenseType]?.[index]?.state
                      }
                      onChange={() => {
                        setShowServerFieldErrors((prev) =>
                          omit(prev, `${controlId}.${licenseType}.${index}.state`)
                        );
                      }}
                    />
                    <FormFeedback
                      clientError={errors?.[controlId]?.[licenseType]?.[index]?.state}
                      serverErrors={serverErrors?.body?.[controlId]?.[licenseType]?.[index]?.state}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <FormTextField
                      name={`${controlIdName}[${index}].number`}
                      label="Number"
                      serverErrors={serverErrors}
                      showServerFieldErrors={showServerFieldErrors}
                      setShowServerFieldErrors={setShowServerFieldErrors}
                      errors={errors}
                      register={register}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <FormTextField
                      type="date"
                      name={`${controlIdName}[${index}].expiration_date`}
                      label="Expiration Date"
                      serverErrors={serverErrors}
                      showServerFieldErrors={showServerFieldErrors}
                      setShowServerFieldErrors={setShowServerFieldErrors}
                      errors={errors}
                      register={register}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <FormLabel
                      className="text-field-label"
                      visibility={FormFieldVisibility.CONNECTED}
                    >
                      Status
                    </FormLabel>
                    <SelectController
                      name={`${controlIdName}[${index}].status`}
                      control={control}
                      options={[
                        { value: 'ACTIVE', label: 'Active' },
                        { value: 'INACTIVE', label: 'Inactive' },
                      ]}
                      isClearable={false}
                      isMulti={false}
                      placeholder="Choose One"
                      isInvalid={
                        !!errors?.[controlId]?.[licenseType]?.[index]?.status ||
                        !!showServerFieldErrors?.[controlId]?.[licenseType]?.[index]?.status
                      }
                      onChange={() => {
                        setShowServerFieldErrors((prev) =>
                          omit(prev, `${controlId}.${licenseType}.${index}.status`)
                        );
                      }}
                    />
                    <FormFeedback
                      clientError={errors?.[controlId]?.[licenseType]?.[index]?.status}
                      serverErrors={serverErrors?.body?.[controlId]?.[licenseType]?.[index]?.status}
                    />
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId={`${controlIdName}[${index}].file__file`}>
                      <Form.Label className="text-field-label">File</Form.Label>
                      <FileUploader
                        name={`${controlIdName}[${index}].file__file`}
                        control={control}
                        className={className}
                        dest="providers_documents"
                        removeButtonText="Replace File"
                        disabledPreview={true}
                        isClearable={true}
                        isInvalid={!!errors.file || !!showServerFieldErrors?.file__file}
                        onRemove={() => {
                          setValue(`${controlIdName}[${index}].file__file`, null, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                      />
                      <FormFeedback clientError={errors?.file} />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
          </li>
        ))}
      </ul>
      <Button
        className="d-flex align-items-center"
        variant="outline-primary"
        type="button"
        onClick={() => append(emptyLicense)}
      >
        <BsPlus className="me-1" /> Add{' '}
        {licenseType === 'MEDICAL' ? 'Medical' : licenseType === 'DEA' ? 'DEA' : licenseType}{' '}
        License
      </Button>
    </>
  );
};
